import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollAnimations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Phone, Mail, MessageCircle, Send, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';

// API base URL - uses relative path for monolithic deployment
const API_BASE_URL = '/api';

const Contact = () => {
  const { ref, isVisible } = useScrollReveal();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSending(true);

    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to send message');
      }

      toast({ 
        title: '✅ Message Sent!', 
        description: "Thank you! We'll get back to you within 24 hours." 
      });
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send message. Please try again.';
      setError(message);
      toast({ 
        title: '❌ Error', 
        description: message,
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-muted/50">
      <div ref={ref} className="container mx-auto px-4">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-primary font-semibold text-sm uppercase tracking-widest">Get In Touch</span>
          <h2 className="text-3xl md:text-5xl font-bold font-display mt-3 text-foreground">Contact Us</h2>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto">Ready to elevate your brand? Reach out for a free consultation and custom quote.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          {/* Info */}
          <div className={`space-y-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-16'}`}>
            <div>
              <h3 className="text-xl font-bold font-display text-foreground mb-4">Let's Build Your Brand</h3>
              <p className="text-muted-foreground leading-relaxed">
                Whether you need school uniforms, corporate branding, or custom merchandise — our team is ready to bring your vision to life.
              </p>
            </div>

            {[
              { icon: MapPin, title: 'Lagos Office', text: 'Lagos, Nigeria', href: undefined },
              { icon: MapPin, title: 'Ilorin Office', text: 'Ilorin, Kwara State, Nigeria', href: undefined },
              { icon: Phone, title: 'Phone', text: '+234 805 048 8734', href: 'tel:+2348050488734' },
              { icon: Mail, title: 'Email', text: 'info@oyinbatfo.com', href: 'mailto:info@oyinbatfo.com' },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-colors duration-300">
                  <item.icon className="w-5 h-5 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">{item.title}</h4>
                  {item.href ? (
                    <a href={item.href} className="text-muted-foreground text-sm hover:text-primary transition-colors">{item.text}</a>
                  ) : (
                    <p className="text-muted-foreground text-sm">{item.text}</p>
                  )}
                </div>
              </div>
            ))}

            {/* Business hours */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h4 className="font-semibold text-foreground mb-3">Business Hours</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex justify-between"><span>Monday – Friday</span><span className="text-foreground font-medium">9:00 AM – 6:00 PM</span></div>
                <div className="flex justify-between"><span>Saturday</span><span className="text-foreground font-medium">10:00 AM – 4:00 PM</span></div>
                <div className="flex justify-between"><span>Sunday</span><span className="text-muted-foreground">Closed</span></div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className={`bg-card border border-border rounded-2xl p-8 shadow-sm transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'}`}
          >
            <h3 className="text-lg font-bold font-display text-foreground mb-6">Send Us a Message</h3>
            
            {error && (
              <Alert variant="destructive" className="mb-5">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-5">
              <div>
                <label htmlFor="name" className="text-sm font-medium text-foreground mb-1.5 block">Full Name *</label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="bg-muted/50 border-border"
                />
              </div>
              <div>
                <label htmlFor="email" className="text-sm font-medium text-foreground mb-1.5 block">Email Address *</label>
                <Input
                  id="email"
                  placeholder="john@example.com"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="bg-muted/50 border-border"
                />
              </div>
              <div>
                <label htmlFor="phone" className="text-sm font-medium text-foreground mb-1.5 block">Phone Number</label>
                <Input
                  id="phone"
                  placeholder="+234 800 000 0000"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-muted/50 border-border"
                />
              </div>
              <div>
                <label htmlFor="message" className="text-sm font-medium text-foreground mb-1.5 block">Your Message *</label>
                <Textarea
                  id="message"
                  placeholder="Tell us about your project..."
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  required
                  className="bg-muted/50 border-border"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-primary hover:bg-primary-glow text-primary-foreground text-lg py-6 disabled:opacity-70"
              >
                {sending ? 'Sending...' : (
                  <>Send Message <Send className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* WhatsApp FAB */}
      <a
        href="https://wa.me/2348050488734"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition-transform"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </a>
    </section>
  );
};

export default Contact;
